/**
 * Market correlation detection for bet builder.
 * Warns users when combining correlated betting markets.
 */

// Correlation coefficients between market pairs
// Higher values indicate stronger positive correlation
const CORRELATION_MATRIX: Record<string, number> = {
  // Shots-related correlations
  'shots|shots_on_target': 0.82,
  'shots|goals': 0.58,
  'shots_on_target|goals': 0.72,

  // Cards-related correlations
  'cards|fouls': 0.68,

  // Corners-related correlations
  'corners|shots': 0.45,
  'corners|shots_on_target': 0.38,
  'corners|goals': 0.25,

  // Goals-related correlations
  'assists|goals': 0.85,

  // Cross-market correlations
  'corners|fouls': 0.30,

  // Player-specific to team correlations
  'player_goals|goals': 0.65,
  'player_shots|shots': 0.50,
  'player_sot|shots_on_target': 0.50,
  'player_assists|goals': 0.55,

  // Save correlations
  'saves|shots_on_target': 0.70,
}

/**
 * Get normalized market type from a bet's marketType
 */
function normalizeMarketType(marketType: string, title: string): string {
  // Check title for more specific market types
  const titleLower = title.toLowerCase()

  if (titleLower.includes('shots on target') || titleLower.includes('sot')) {
    return 'shots_on_target'
  }
  if (titleLower.includes('shot')) {
    return 'shots'
  }
  if (titleLower.includes('corner')) {
    return 'corners'
  }
  if (titleLower.includes('card') || titleLower.includes('carded')) {
    return 'cards'
  }
  if (titleLower.includes('goal') || titleLower.includes('btts') || titleLower.includes('score')) {
    return 'goals'
  }
  if (titleLower.includes('foul')) {
    return 'fouls'
  }
  if (titleLower.includes('assist')) {
    return 'assists'
  }
  if (titleLower.includes('save')) {
    return 'saves'
  }

  // Player-specific mappings from the bet's market type
  if (marketType === 'shots_on_target') return 'shots_on_target'
  if (marketType === 'shots') return 'shots'
  if (marketType === 'corners') return 'corners'
  if (marketType === 'cards') return 'cards'
  if (marketType === 'goals') return 'goals'

  return marketType
}

/**
 * Get correlation coefficient between two market types
 */
function getCorrelation(type1: string, type2: string): number {
  if (type1 === type2) return 1.0

  // Sort alphabetically for consistent lookup
  const key = [type1, type2].sort().join('|')
  return CORRELATION_MATRIX[key] || 0
}

export interface CorrelationWarning {
  betId: string
  betTitle: string
  correlation: number
  strength: 'high' | 'moderate' | 'low'
}

/**
 * Check for correlations between a new market and existing bets
 */
export function checkCorrelations(
  newMarketType: string,
  newTitle: string,
  existingBets: Array<{ id: string; title: string; marketType: string }>,
  threshold = 0.4
): CorrelationWarning[] {
  const warnings: CorrelationWarning[] = []
  const normalizedNew = normalizeMarketType(newMarketType, newTitle)

  for (const bet of existingBets) {
    const normalizedExisting = normalizeMarketType(bet.marketType, bet.title)
    const correlation = getCorrelation(normalizedNew, normalizedExisting)

    if (Math.abs(correlation) >= threshold) {
      let strength: 'high' | 'moderate' | 'low' = 'low'
      if (Math.abs(correlation) >= 0.7) {
        strength = 'high'
      } else if (Math.abs(correlation) >= 0.5) {
        strength = 'moderate'
      }

      warnings.push({
        betId: bet.id,
        betTitle: bet.title,
        correlation,
        strength,
      })
    }
  }

  return warnings
}

/**
 * Get a human-readable correlation warning message
 */
export function getCorrelationMessage(warnings: CorrelationWarning[]): string | null {
  if (warnings.length === 0) return null

  const highCorr = warnings.filter(w => w.strength === 'high')
  const modCorr = warnings.filter(w => w.strength === 'moderate')

  if (highCorr.length > 0) {
    const titles = highCorr.map(w => w.betTitle).slice(0, 2).join(', ')
    return `High correlation with: ${titles}`
  }

  if (modCorr.length > 0) {
    const titles = modCorr.map(w => w.betTitle).slice(0, 2).join(', ')
    return `Moderate correlation with: ${titles}`
  }

  return 'Some correlation with existing bets'
}
